"use client";

import { Home, Loader2, LocateFixed, MapPin, X } from "lucide-react";
import dynamic from "next/dynamic";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	formatCoordinate,
	GEOLOCATION_DEFAULT_PRECISION,
	type GeolocationMode,
	type GeolocationValue,
} from "@/lib/fields/geolocation";
import type { BaseFieldProps } from "../types";
import { getBuilderMode } from "../utils";

// Leaflet требует window — карта грузится только на клиенте
const LeafletMap = dynamic(() => import("./leaflet-map"), {
	ssr: false,
	loading: () => (
		<div className="flex h-64 w-full items-center justify-center rounded-lg border bg-muted/30 text-muted-foreground text-sm">
			...
		</div>
	),
});

const GPS_TIMEOUT_MS = 10_000;

export function GeolocationField(props: BaseFieldProps) {
	const t = useTranslations("renderer");
	const locale = useLocale();
	const builderMode = getBuilderMode(props);

	const mode: GeolocationMode =
		(props.field.settings?.geolocationMode as GeolocationMode | undefined) ??
		"both";
	const precision =
		props.field.settings?.geolocationPrecision ?? GEOLOCATION_DEFAULT_PRECISION;

	const [locating, setLocating] = useState(false);
	const [gpsError, setGpsError] = useState<string | null>(null);
	// Обратный геокодинг: состояние запроса адреса для текущей точки
	const [addressState, setAddressState] = useState<
		"idle" | "loading" | "done" | "failed"
	>("idle");
	const geocodeAbortRef = useRef<AbortController | null>(null);
	const geocodeSeqRef = useRef(0);
	// Координаты последней попытки геокодинга ("lat,lng")
	const lastGeocodeKeyRef = useRef<string | null>(null);

	const parsed = parseGeolocationValue(props.value);
	// refs со «свежими» ссылками: эффект геокодинга ниже НЕ должен зависеть
	// от props/parsed (новые объекты каждый рендер — иначе перезапуск в цикле).
	const onChangeRef = useRef(props.onChange);
	onChangeRef.current = props.onChange;
	const valueRef = useRef(props.value);
	valueRef.current = props.value;

	/**
	 * Обратный геокодинг (Nominatim/OSM): запрос идёт напрямую из браузера
	 * респондента (его IP), ответ — best-effort. Зависимости — примитивы
	 * (lat/lng/address/locale), чтобы эффект не перезапускался на каждом
	 * рендере и не мигал стейт «Определяем адрес…».
	 */
	const geoLat = parsed?.lat;
	const geoLng = parsed?.lng;
	const geoAddress = parsed?.address;
	useEffect(() => {
		if (geoLat === undefined || geoLng === undefined) {
			geocodeSeqRef.current += 1;
			geocodeAbortRef.current?.abort();
			setAddressState("idle");
			lastGeocodeKeyRef.current = null;
			return;
		}
		// Адрес уже определён (или только что получен) — показываем сразу.
		if (geoAddress) {
			setAddressState("done");
			lastGeocodeKeyRef.current = `${geoLat.toFixed(6)},${geoLng.toFixed(6)}`;
			return;
		}
		// Одна точка = одна попытка геокодинга (успешной или нет).
		const pointKey = `${geoLat.toFixed(6)},${geoLng.toFixed(6)}`;
		if (lastGeocodeKeyRef.current === pointKey) {
			return;
		}
		lastGeocodeKeyRef.current = pointKey;
		const seq = geocodeSeqRef.current + 1;
		geocodeSeqRef.current = seq;
		const timer = setTimeout(() => {
			geocodeAbortRef.current?.abort();
			const controller = new AbortController();
			geocodeAbortRef.current = controller;
			setAddressState("loading");
			const run = async () => {
				try {
					const name = await fetchReverseGeocode(
						geoLat,
						geoLng,
						locale ?? "ru",
						controller.signal
					);
					if (seq !== geocodeSeqRef.current) {
						return;
					}
					if (name) {
						setAddressState("done");
						const cur = valueRef.current;
						onChangeRef.current({ ...cur, address: name });
					} else {
						setAddressState("failed");
					}
				} catch {
					if (seq === geocodeSeqRef.current) {
						setAddressState("failed");
					}
				}
			};
			run().catch((e: unknown) => {
				if (e instanceof Error) {
					console.warn("[geolocation] geocode promise rejected:", e.message);
				}
			});
		}, 800);
		return () => {
			clearTimeout(timer);
		};
	}, [geoLat, geoLng, geoAddress, locale]);

	const setValue = (next: GeolocationValue | null) => {
		props.onChange(next);
	};

	const handleLocate = () => {
		if (!("geolocation" in navigator)) {
			setGpsError(t("geolocation.unsupported"));
			return;
		}
		setGpsError(null);
		setLocating(true);
		// Одиночное определение: одна фиксация точки, без слежения —
		// иначе маркер прыгает с каждым GPS-апдейтом.
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				setLocating(false);
				setValue({
					lat: pos.coords.latitude,
					lng: pos.coords.longitude,
					accuracy: pos.coords.accuracy,
					source: "gps",
				});
			},
			(err) => {
				setLocating(false);
				if (err.code === err.PERMISSION_DENIED) {
					setGpsError(t("geolocation.permissionDenied"));
				} else if (err.code === err.POSITION_UNAVAILABLE) {
					setGpsError(t("geolocation.positionUnavailable"));
				} else {
					setGpsError(t("geolocation.timeout"));
				}
			},
			{
				enableHighAccuracy: true,
				timeout: GPS_TIMEOUT_MS,
				maximumAge: 30_000,
			}
		);
	};

	const handlePick = (lat: number, lng: number) => {
		setValue({ lat, lng, source: "manual" });
	};

	const handleClear = () => {
		setValue(null);
		setGpsError(null);
		setAddressState("idle");
	};

	const showGpsButton = mode !== "map";
	const showMap = mode !== "current";

	const coordsText = parsed
		? formatCoordinate(parsed.lat, precision) +
			", " +
			formatCoordinate(parsed.lng, precision)
		: "";

	return (
		<div className={builderMode ? "pointer-events-none" : ""}>
			<div className="flex flex-col gap-3">
				{showGpsButton && (
					<div className="flex flex-col gap-2">
						<Button
							aria-busy={locating}
							className="w-full"
							disabled={props.disabled || locating}
							onClick={handleLocate}
							type="button"
							variant="outline"
						>
							{locating ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<LocateFixed className="h-4 w-4" />
							)}
							{locating
								? t("geolocation.locating")
								: t("geolocation.locateButton")}
						</Button>
						{parsed && (
							<div className="flex flex-wrap items-center gap-2">
								<Button
									className="h-8 px-2 text-muted-foreground"
									disabled={props.disabled}
									onClick={handleClear}
									size="sm"
									type="button"
									variant="ghost"
								>
									<X className="h-4 w-4" />
									{t("geolocation.clear")}
								</Button>
								<span className="inline-flex flex-wrap items-center gap-1.5 text-sm">
									<MapPin className="h-4 w-4 text-primary" />
									{coordsText}
									{typeof parsed.accuracy === "number" && (
										<span className="text-muted-foreground">
											(+-{Math.round(parsed.accuracy)}
											&nbsp;{t("geolocation.meters")})
										</span>
									)}
								</span>
							</div>
						)}
						{addressState !== "idle" && (
							<p className="flex items-start gap-1.5 text-muted-foreground text-sm">
								<Home className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
								{addressState === "loading" && t("geolocation.addressLoading")}
								{addressState === "done" && parsed?.address}
								{addressState === "failed" && t("geolocation.addressFailed")}
							</p>
						)}
					</div>
				)}

				{showMap && (
					<LeafletMap
						interactive={!(builderMode || props.disabled)}
						onPick={handlePick}
						position={parsed ? { lat: parsed.lat, lng: parsed.lng } : null}
						source={parsed?.source ?? null}
					/>
				)}

				{gpsError && (
					<p className="text-destructive text-sm" role="alert">
						{gpsError}
					</p>
				)}

				{showGpsButton && (
					<p className="text-muted-foreground text-xs">
						{t("geolocation.consentHint")}
					</p>
				)}
			</div>
		</div>
	);
}

function parseGeolocationValue(value: unknown): GeolocationValue | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return null;
	}
	const v = value as Record<string, unknown>;
	if (
		typeof v.lat === "number" &&
		Number.isFinite(v.lat) &&
		typeof v.lng === "number" &&
		Number.isFinite(v.lng)
	) {
		// address ОБЯЗАТЕЛЬНО переносим: без него эффект геокодинга не
		// видит, что адрес уже определён, и уходит в бесконечный цикл.
		return {
			lat: v.lat,
			lng: v.lng,
			accuracy: typeof v.accuracy === "number" ? v.accuracy : undefined,
			source: v.source === "gps" ? "gps" : "manual",
			address: typeof v.address === "string" ? v.address : undefined,
		};
	}
	return null;
}

/** Запрос обратного геокодинга: возвращает адрес или null. */
async function fetchReverseGeocode(
	lat: number,
	lng: number,
	locale: string,
	signal: AbortSignal
): Promise<string | null> {
	const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=${locale || "ru"}`;
	const res = await fetch(url, {
		signal,
		headers: { Accept: "application/json" },
	});
	if (!res.ok) {
		throw new Error(`geocode ${res.status}`);
	}
	const json = (await res.json()) as { display_name?: string };
	return json.display_name?.trim() || null;
}
